import { ExternalLink, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FixedAsset } from '@/hooks/useFixedAssetDetail';
import { FixedAssetValue } from '@/hooks/useFixedAssetValues';
import { computeAppreciation } from '@/lib/utils/fixed-asset-utils';

interface AssetCoreInfoProps {
  asset: FixedAsset;
  values: FixedAssetValue[];
}

function formatCurrency(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 py-2 border-b last:border-0">
      <span className="text-sm text-muted-foreground sm:w-48 shrink-0">{label}</span>
      <span className="text-sm font-medium">{value ?? '—'}</span>
    </div>
  );
}

export function AssetCoreInfo({ asset, values }: AssetCoreInfoProps) {
  // Current value: most recent fixed_asset_values record, fallback to purchase_price
  const sortedValues = [...values].sort(
    (a, b) => new Date(b.recorded_at).getTime() - new Date(a.recorded_at).getTime()
  );
  const currentValue = sortedValues.length > 0
    ? Number(sortedValues[0].value)
    : Number(asset.purchase_price ?? 0);

  const purchasePrice = Number(asset.purchase_price ?? 0);
  const delta = computeAppreciation(currentValue, purchasePrice);
  const isPositive = delta >= 0;

  const locationTypes = ['apartment', 'house', 'land', 'commercial_building'];
  const showLocation = locationTypes.includes(asset.asset_type);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Asset Details</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Current value — prominent */}
        <div className="mb-4 p-4 rounded-lg bg-muted/50" aria-label="current-value-section">
          <div className="text-sm text-muted-foreground">Current Value</div>
          <div className="text-3xl font-bold" data-testid="current-value">
            {formatCurrency(currentValue, asset.currency)}
          </div>
          {purchasePrice > 0 && (
            <div
              className={`flex items-center gap-1 mt-1 text-sm font-medium ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}
              data-testid="appreciation-delta"
            >
              {isPositive ? (
                <TrendingUp className="h-4 w-4" />
              ) : (
                <TrendingDown className="h-4 w-4" />
              )}
              <span data-testid="delta-sign">{isPositive ? '+' : ''}</span>
              <span data-testid="delta-value">
                {formatCurrency(delta, asset.currency)}
              </span>
              <span className="text-muted-foreground font-normal">vs purchase price</span>
            </div>
          )}
        </div>

        <DetailRow label="Name" value={asset.name} />
        <DetailRow
          label="Asset Type"
          value={
            <Badge variant="secondary">
              {asset.asset_type.replace(/_/g, ' ')}
            </Badge>
          }
        />
        <DetailRow label="Currency" value={asset.currency} />
        <DetailRow
          label="Purchase Price"
          value={formatCurrency(purchasePrice, asset.currency)}
        />
        <DetailRow label="Purchase Date" value={formatDate(asset.purchase_date)} />
        {showLocation && (
          <DetailRow label="Location" value={asset.location ?? '—'} />
        )}
        <DetailRow
          label="Status"
          value={
            <Badge variant={asset.status === 'owned' ? 'default' : 'outline'}>
              {asset.status.charAt(0).toUpperCase() + asset.status.slice(1)}
            </Badge>
          }
        />
        {showLocation && (
          <>
            <DetailRow
              label="Tenure Type"
              value={
                <span className="capitalize">{asset.tenure_type || 'Unknown'}</span>
              }
            />
            <DetailRow
              label="Ownership Status"
              value={
                <Badge variant={asset.ownership_status === 'fully_owned' ? 'default' : 'secondary'} className={asset.ownership_status === 'fully_owned' ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-blue-100 text-blue-800 hover:bg-blue-100'}>
                  {asset.ownership_status === 'fully_owned' ? 'Fully Owned' : 'Purchasing'}
                </Badge>
              }
            />
          </>
        )}
        {asset.notes && <DetailRow label="Notes" value={asset.notes} />}
        {asset.ownership_document_url && (
          <DetailRow
            label="Ownership Document"
            value={
              <a
                href={asset.ownership_document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-primary hover:underline"
                data-testid="ownership-document-link"
              >
                View Document <ExternalLink className="h-3 w-3" />
              </a>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}
